'use client'

import { createPortal } from 'react-dom'
import { useTranslations } from 'next-intl'
import { useFocusTrap } from '@/hooks/use-focus-trap'
import type { ProductIngredientItem } from '@lib/models/types'
import './IngredientsModal.css'

interface IngredientsModalProps {
  productName: string
  ingredients: ProductIngredientItem[]
  onClose: () => void
}

export function IngredientsModal({ productName, ingredients, onClose }: IngredientsModalProps) {
  const t = useTranslations('productCard')
  const contentRef = useFocusTrap<HTMLDivElement>(onClose)

  /* Portalled to <body> for the same reason as CompatibilityModal: the card sits
     inside .category-section's stacking context, which would otherwise pin the
     backdrop below the sticky products header. */
  const modal = (
    <div
      id="ingredients-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ingredients-modal-title"
      className="ingredients-modal-overlay"
      onClick={onClose}
    >
      <div className="ingredients-modal-backdrop" />
      <div
        ref={contentRef}
        className="ingredients-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="ingredients-modal-title"
          className="text-black text-lg mb-4 text-left"
        >
          {t('ingredientsModalTitle', { name: productName })}
        </h2>
        <ol id="ingredients-modal-list" className="ingredients-modal-list">
          {ingredients.map((ingredient, index) => (
            <li key={ingredient.inciName} id={`ingredients-modal-item-${index}`} className="ingredients-modal-item">
              <span id={`ingredients-modal-item-${index}-inci`} className="ingredients-modal-item-inci">
                {ingredient.inciName}
              </span>
              {ingredient.commonName && (
                <span id={`ingredients-modal-item-${index}-common`} className="ingredients-modal-item-common">
                  {ingredient.commonName}
                </span>
              )}
              {ingredient.safetyNote && (
                <span id={`ingredients-modal-item-${index}-safety-note`} className="ingredients-modal-item-safety-note">
                  {ingredient.safetyNote}
                </span>
              )}
            </li>
          ))}
        </ol>
        <button
          onClick={onClose}
          aria-label={t('closeIngredients')}
          className="ingredients-modal-close"
        >
          ✕
        </button>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
